<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { useRoute, useRouter } from 'vue-router';
import type { FormMeta } from 'vee-validate';

import FormActions from '@/components/FormActions.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import ConditionEditorDialog from '@/components/dialogs/ConditionEditorDialog.vue';

import type { ConsultationDto, Locales } from '@/services/types/api.types';
import type { LocaleValidation } from '@/composables/language-validation-errors';
import { Constants } from '@/constants';
import { useFormsStore } from '@/stores';
import { getTranslation } from '@/composables/data-display-utils';
import { canBeMandatory } from '@/helpers/visibilityRules.helper';

const { t } = useI18n();
const $q = useQuasar();
const route = useRoute();
const router = useRouter();
const formsStore = useFormsStore();

const props = withDefaults(
  defineProps<{
    formId: number;
    consultation: ConsultationDto | null;
    languageEnabled?: Locales[];
    editLocale: Locales;
    meta: FormMeta<Record<string, unknown>>;
    languageErrors: LocaleValidation;
    onCancel: () => void;
    onSubmit: (e?: Event) => void | Promise<unknown>;
  }>(),
  {
    languageEnabled: () => [],
    consultation: null,
  },
);

const emit = defineEmits<{
  'update:editLocale': [value: Locales];
}>();

const isGuidedConsultation = computed(
  () => props.consultation?.participation_mode === Constants.Consultation.ParticipationMode.Guided,
);

const form = computed(() => formsStore.getItem(props.formId));
// A mandatory question of a type that cannot be one stays editable, so it can be turned off
const mandatoryUnsupported = computed(() => !!form.value && !canBeMandatory(form.value));
const mandatoryDisabled = computed(() => mandatoryUnsupported.value && form.value?.is_mandatory !== 1);

const ruleSet = computed(
  () =>
    (props.consultation?.options?.guided_consultation?.visibility?.ruleSets ?? []).find(
      (rs) => rs.targetId === props.formId,
    ) ?? null,
);

const conditionLines = computed(() => {
  if (!ruleSet.value) return [];
  return ruleSet.value.conditions.map((cond) => {
    const sourceForm = formsStore.getItem(cond.sourceFormId);
    const sourceName = sourceForm
      ? getTranslation(sourceForm.question, 'title', props.languageEnabled) || `Q${cond.sourceFormId}`
      : `Q${cond.sourceFormId}`;
    const choiceId = cond.valueIds?.[0];
    let answerText = choiceId != null ? String(choiceId) : '?';
    if (sourceForm && choiceId != null) {
      const choice = sourceForm.answers?.steps?.[0]?.choices?.find((c: any) => c.id === choiceId);
      if (choice) answerText = getTranslation(choice, 'text', props.languageEnabled) || answerText;
    }
    return t('form.guided-consultation.summary.conditionalDetail', { question: sourceName, answer: answerText });
  });
});

onMounted(async () => {
  if (!ruleSet.value) return;
  for (const cond of ruleSet.value.conditions) {
    if (!formsStore.getItem(cond.sourceFormId)) {
      await formsStore.fetchForm(cond.sourceFormId);
    }
  }
});

function navigateToGuidedConsultation() {
  if (!props.consultation) return;
  router.push({
    name: route.name === 'form_template_details' ? 'consultation_template_details' : 'consultation_details',
    params: { id: props.consultation.id },
    hash: '#guided-consultation',
  });
}

function openConditionEditor() {
  if (!props.consultation) return;
  $q.dialog({
    component: ConditionEditorDialog,
    componentProps: {
      formId: props.formId,
      consultation: props.consultation,
      languageEnabled: props.languageEnabled,
    },
  });
}
</script>

<template>
  <div class="column">
    <template v-if="!isGuidedConsultation">
      <q-card class="bg-blue-1">
        <q-card-section>
          <div class="text-body1 text-weight-medium q-mb-xs">
            {{ t('form.guided-consultation.disabled.title') }}
          </div>
          <div class="text-body2 text-grey-8 q-mb-md">
            {{ t('form.guided-consultation.disabled.description') }}
          </div>
          <q-btn
            outline
            color="primary"
            no-caps
            :label="t('form.guided-consultation.disabled.cta')"
            icon-right="arrow_forward"
            @click="navigateToGuidedConsultation"
          />
        </q-card-section>
      </q-card>
    </template>
    <template v-else>
      <form-actions
        :editLocale="editLocale"
        @update:editLocale="(v) => v !== undefined && emit('update:editLocale', v)"
        :onCancel="onCancel"
        :onSubmit="onSubmit"
        :languageErrors="languageErrors"
        :meta="props.meta"
        :language-enabled="languageEnabled"
        :show-language-switcher="false"
      />

      <q-card class="q-mb-md">
        <q-card-section>
          <standard-toggle
            name="is_mandatory"
            :true-value="1"
            :false-value="0"
            :label="t('form.guided-consultation.mandatory.label')"
            :disabled="mandatoryDisabled"
            outlined
          />
          <div class="text-caption text-grey-7 q-mt-xs q-ml-lg">
            {{
              mandatoryUnsupported
                ? t('form.guided-consultation.mandatory.unsupportedType')
                : t('form.guided-consultation.mandatory.helper')
            }}
          </div>
        </q-card-section>
      </q-card>

      <q-card>
        <q-card-section>
          <div class="text-subtitle2 text-weight-medium">{{ t('form.guided-consultation.visibility.title') }}</div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <template v-if="conditionLines.length > 0">
            <div v-for="(line, i) in conditionLines" :key="i" class="row items-start q-mb-xs">
              <q-icon name="o_visibility" size="xs" color="purple" class="q-mr-xs q-mt-xs" />
              <span class="text-body2 col">{{ line }}</span>
            </div>
            <div v-if="conditionLines.length > 1" class="text-caption text-grey-7 q-mt-xs q-mb-sm">
              {{ t('form.guided-consultation.visibility.allConditionsTrue') }}
            </div>
          </template>
          <div v-else class="text-caption text-grey-7 q-mb-sm">
            {{ t('form.guided-consultation.summary.noRules') }}
          </div>
          <q-btn
            flat
            dense
            no-caps
            color="primary"
            size="sm"
            :label="t('form.guided-consultation.summary.editLogic')"
            icon="o_edit"
            class="q-mt-xs"
            @click="openConditionEditor"
          />
        </q-card-section>
      </q-card>
    </template>
  </div>
</template>
